import { ref, reactive, watch, type ComputedRef } from 'vue'
import Rpc from '@/config/apiServer/chain33rpc'
import {
  getAccountERC20Balances,
  type Erc20BalanceRow,
} from '@/config/apiServer/scanner'

const COIN_DECIMAL = 1e8

export interface ExecAccountItem {
  execer: string
  account: { balance?: number; frozen?: number }
}

export interface TokenBalRow {
  symbol: string
  balance: number | string
  nickname?: string
}

export default function useAddressOverview(
  addr: ComputedRef<string | undefined>
): {
  adrInfo: {
    balance: number
    frozen: number
    txCount: number
    reciver: number
    send: number
  }
  contractInfo: ReturnType<typeof ref<ExecAccountItem[]>>
  contractSearch: ReturnType<typeof ref<string>>
  tokenBalance: ReturnType<typeof ref<TokenBalRow[]>>
  BASE_COIN_LABEL: string
  coinAmount: (n: number) => string
  frozenAmount: (n: number) => string
  searchContract: (ticket: string) => void
} {
  const BASE_COIN_LABEL = 'BUB'

  const adrInfo = reactive({
    balance: 0,
    frozen: 0,
    txCount: 0,
    reciver: 0,
    send: 0,
  })

  const contractInfo = ref<ExecAccountItem[]>([])
  const contractSearch = ref('')
  const tokenBalance = ref<TokenBalRow[]>([])

  function coinAmount(n: number): string {
    return parseFloat((Number(n) / COIN_DECIMAL).toFixed(4)).toString()
  }

  function frozenAmount(n: number): string {
    const v = Number(n)
    if (!v) return '0'
    return (v / COIN_DECIMAL).toFixed(8)
  }

  async function loadAddrOverview(): Promise<void> {
    const a = addr.value
    if (!a) return
    const res = await Rpc.getAddrOverview(a)
    if (res.error != null || !res.result) return
    const r = res.result as Record<string, number>
    adrInfo.balance = Number(r.balance) || 0
    adrInfo.reciver =
      Number(r.reciver !== undefined ? r.reciver : (r as { receiver?: number }).receiver) || 0
    adrInfo.txCount = Number(r.txCount) || 0
    adrInfo.send = adrInfo.reciver - adrInfo.balance
    adrInfo.frozen = Number(r.frozen) || 0
  }

  async function loadExecBalances(): Promise<void> {
    const a = addr.value
    if (!a) return
    const res = await Rpc.getAllExecBalance(a)
    if (res.error != null || !res.result) return
    const raw = res.result as { execAccount?: ExecAccountItem[] }
    const list = raw.execAccount
    if (!list || !Array.isArray(list)) return

    // const hasCoins = list.some((x) => x.execer === 'coins')
    // if (!hasCoins) {
    //   list = [
    //     { execer: 'coins', account: { balance: 0, frozen: 0 } },
    //     ...list,
    //   ]
    // }
    contractInfo.value = list.reverse()
    contractSearch.value = contractInfo.value[0]?.execer || 'coins'
    const coins = list.find((x) => x.execer === 'user.p.Benefy.coins')
    if (coins?.account) {
      adrInfo.balance = coins.account.balance || 0
      adrInfo.frozen = coins.account.frozen || 0
    }
  }

  async function loadTokenAssets(): Promise<void> {
    const a = addr.value
    if (!a) {
      tokenBalance.value = []
      return
    }
    try {
      const data = await getAccountERC20Balances(a, { page: 1, size: 100 })
      if (data.code !== 0 || !data.data?.tokens) {
        tokenBalance.value = []
        return
      }
      tokenBalance.value = data.data.tokens
        .filter((t: Erc20BalanceRow) => Number(t.balance_formatted) > 0)
        .map((current: Erc20BalanceRow) => ({
          contract_address: current['contract_address'] as string,
          balance: Number(current.balance_formatted),
          symbol: String(current.symbol),
          nickname: current.name ? String(current.name) : '',
        }))
    } catch {
      tokenBalance.value = []
    }
  }

  function searchContract(ticket: string): void {
    contractInfo.value.forEach((item) => {
      if (item.execer === ticket) {
        adrInfo.balance = item.account.balance || 0
        adrInfo.frozen = item.account.frozen || 0
      }
    })
  }

  async function refresh(): Promise<void> {
    if (!addr.value) {
      adrInfo.balance = 0
      adrInfo.frozen = 0
      adrInfo.txCount = 0
      adrInfo.reciver = 0
      adrInfo.send = 0
      contractInfo.value = []
      tokenBalance.value = []
      return
    }
    await loadAddrOverview()
    await loadExecBalances()
    await loadTokenAssets()
  }

  watch(
    addr,
    () => {
      // contractSearch.value = 'coins'
      void refresh()
    },
    { immediate: true }
  )

  return {
    adrInfo,
    contractInfo,
    contractSearch,
    tokenBalance,
    BASE_COIN_LABEL,
    coinAmount,
    frozenAmount,
    searchContract,
  }
}
